import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { ExternalLink } from 'lucide-react';
import { normList, slugify, EMPTY_SHOWCASE, RATINGS } from '@/components/admin/rig/rigConstants';
import RigBookingTab from '@/components/admin/rig/RigBookingTab';
import RigShowcaseTab from '@/components/admin/rig/RigShowcaseTab';

/**
 * RigDetailEditor — orchestrator for editing a single booking rig and its
 * linked showcase page. Owns all state and persistence logic; the two tab
 * bodies are rendered by RigBookingTab and RigShowcaseTab.
 */
export default function RigDetailEditor({ rigKey, defaultData, record, isNew, onSaved, onCreated, onDelete }) {
  const [subTab, setSubTab] = useState('booking');

  // Booking fields
  const [key, setKey] = useState(record?.key ?? '');
  const [name, setName] = useState(record?.name ?? defaultData?.name ?? '');
  const [tag, setTag] = useState(record?.tag ?? defaultData?.tag ?? 'ADULT RIG');
  const [description, setDescription] = useState(record?.description ?? defaultData?.description ?? '');
  const [specs, setSpecs] = useState((record?.specs ?? []).join('\n'));
  const [imageUrl, setImageUrl] = useState(record?.image_url ?? '');
  const [galleryImages, setGalleryImages] = useState(
    () => normList(record?.gallery_images?.length ? record.gallery_images : (record?.image_url ? [record.image_url] : []))
  );
  const [active, setActive] = useState(record?.active ?? true);
  const [sortOrder, setSortOrder] = useState(record?.sort_order ?? 0);
  const [savingBooking, setSavingBooking] = useState(false);
  const [bookingSaved, setBookingSaved] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Showcase fields
  const [showcase, setShowcase] = useState(null); // linked ShowcaseRig record
  const [sc, setSc] = useState(EMPTY_SHOWCASE);
  const [scLoading, setScLoading] = useState(false);
  const [savingSc, setSavingSc] = useState(false);
  const [scSaved, setScSaved] = useState(false);
  const [gameInput, setGameInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  const resolvedKey = record?.key ?? rigKey ?? key;

  // Load linked showcase rig whenever the booking key changes
  useEffect(() => {
    if (isNew || !resolvedKey) { setShowcase(null); setSc(EMPTY_SHOWCASE); return; }
    let cancelled = false;
    setScLoading(true);
    const norm = (s) => (s || '').toLowerCase().replace(/[-_]+/g, '-');
    base44.entities.ShowcaseRig.list().then((all) => {
      if (cancelled) return;
      const found = (all || []).find((r) => norm(r.slug) === norm(resolvedKey));
      setShowcase(found || null);
      setSc({ ...EMPTY_SHOWCASE, ...(found || {}) });
      // Merge any showcase-only gallery photos into the shared library on first load
      const scPhotos = normList(found?.gallery_images || []);
      if (scPhotos.length) {
        setGalleryImages((prev) => {
          const seen = new Set(prev.map((p) => p.url));
          const merged = [...prev];
          for (const p of scPhotos) {
            if (!seen.has(p.url)) merged.push({ url: p.url, hash: p.hash, show_booking: false, show_showcase: true });
          }
          return merged.length !== prev.length ? merged : prev;
        });
      }
    }).catch(() => {}).finally(() => { if (!cancelled) setScLoading(false); });
    return () => { cancelled = true; };
  }, [resolvedKey, isNew]);

  const setScField = (k, v) => setSc((s) => ({ ...s, [k]: v }));

  // AI auto-fill: use the rig's booking details to populate the showcase page fields
  const handleAiFill = async () => {
    const specsList = specs.split('\n').map((s) => s.trim()).filter(Boolean);
    setAiLoading(true);
    try {
      const prompt = `You are writing the premium showcase page for a Motion Reality sim racing rig. Use ONLY the booking details below to fill the structured fields.

Rig name: ${name}
Tag: ${tag}
Description: ${description}
Build specs:
${specsList.map((s) => '- ' + s).join('\n')}

Rules:
- category: choose the single best fit from gt, formula, rally, drift, other.
- short_description: one punchy tagline, max 12 words, no quotes.
- full_description: 2-3 sentences of premium marketing copy.
- experience_text: 2-3 sentences on what makes this rig unique to drive.
- Map the build specs verbatim into the spec_* fields (cockpit, seat, wheelbase, pedals, steering_wheel, dashboard, displays, pc, audio) using the brand/model from the specs. Leave a field empty string if not mentioned.
- rating_* : score 0-10 based on the hardware quality (high-end direct drive, load cell pedals, triple monitors = higher). Be realistic and conservative.
- overall_rating: a sensible weighted average of the others.
- supported_games: 4-6 popular sim titles this rig suits (e.g. iRacing, Assetto Corsa Competizione, Le Mans Ultimate, Automobilista 2, F1 24, rFactor 2).
Return strict JSON only, no commentary.`;
      const schema = {
        type: 'object',
        properties: {
          category: { type: 'string', enum: ['gt', 'formula', 'rally', 'drift', 'other'] },
          short_description: { type: 'string' },
          full_description: { type: 'string' },
          experience_text: { type: 'string' },
          overall_rating: { type: 'number' },
          rating_realism: { type: 'number' },
          rating_immersion: { type: 'number' },
          rating_hardware: { type: 'number' },
          rating_comfort: { type: 'number' },
          rating_beginner_friendly: { type: 'number' },
          rating_competitive: { type: 'number' },
          spec_cockpit: { type: 'string' },
          spec_seat: { type: 'string' },
          spec_wheelbase: { type: 'string' },
          spec_pedals: { type: 'string' },
          spec_steering_wheel: { type: 'string' },
          spec_dashboard: { type: 'string' },
          spec_displays: { type: 'string' },
          spec_pc: { type: 'string' },
          spec_audio: { type: 'string' },
          supported_games: { type: 'array', items: { type: 'string' } },
        },
      };
      const res = await base44.integrations.Core.InvokeLLM({ prompt, response_json_schema: schema });
      const d = res || {};
      setSc((s) => ({
        ...s,
        category: d.category || s.category,
        short_description: d.short_description || s.short_description,
        full_description: d.full_description || s.full_description,
        experience_text: d.experience_text || s.experience_text,
        overall_rating: Number(d.overall_rating || 0),
        rating_realism: Number(d.rating_realism || 0),
        rating_immersion: Number(d.rating_immersion || 0),
        rating_hardware: Number(d.rating_hardware || 0),
        rating_comfort: Number(d.rating_comfort || 0),
        rating_beginner_friendly: Number(d.rating_beginner_friendly || 0),
        rating_competitive: Number(d.rating_competitive || 0),
        spec_cockpit: d.spec_cockpit || s.spec_cockpit,
        spec_seat: d.spec_seat || s.spec_seat,
        spec_wheelbase: d.spec_wheelbase || s.spec_wheelbase,
        spec_pedals: d.spec_pedals || s.spec_pedals,
        spec_steering_wheel: d.spec_steering_wheel || s.spec_steering_wheel,
        spec_dashboard: d.spec_dashboard || s.spec_dashboard,
        spec_displays: d.spec_displays || s.spec_displays,
        spec_pc: d.spec_pc || s.spec_pc,
        spec_audio: d.spec_audio || s.spec_audio,
        supported_games: Array.isArray(d.supported_games) && d.supported_games.length ? d.supported_games : s.supported_games,
        hero_image_url: s.hero_image_url || imageUrl,
      }));
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveBooking = async () => {
    const finalKey = isNew ? slugify(key) : resolvedKey;
    if (!finalKey) return;
    setSavingBooking(true);
    setBookingSaved(false);
    const coverUrl = galleryImages.find((p) => p.show_booking)?.url || galleryImages[0]?.url || imageUrl || '';
    const payload = {
      key: finalKey, name, tag, description,
      specs: specs.split('\n').map((s) => s.trim()).filter(Boolean),
      image_url: coverUrl,
      gallery_images: galleryImages,
      active, sort_order: Number(sortOrder || 0),
    };
    try {
      if (record?.id) {
        await base44.entities.BookingRig.update(record.id, payload);
      } else {
        await base44.entities.BookingRig.create(payload);
      }
      if (showcase?.id) {
        try {
          const showcaseUrls = galleryImages.filter((p) => p.show_showcase).map((p) => p.url);
          await base44.entities.ShowcaseRig.update(showcase.id, { gallery_images: showcaseUrls });
          setSc((s) => ({ ...s, gallery_images: showcaseUrls }));
        } catch {}
      }
      setBookingSaved(true);
      setTimeout(() => setBookingSaved(false), 2000);
      if (isNew) onCreated?.(); else onSaved?.();
    } finally {
      setSavingBooking(false);
    }
  };

  const handleSaveShowcase = async () => {
    if (!resolvedKey) return;
    setSavingSc(true);
    setScSaved(false);
    const showcaseUrls = galleryImages.filter((p) => p.show_showcase).map((p) => p.url);
    const payload = {
      ...sc,
      name,
      slug: showcase?.slug || resolvedKey,
      sort_order: Number(sc.sort_order || 0),
      gallery_images: showcaseUrls,
      supported_games: sc.supported_games || [],
    };
    RATINGS.forEach((r) => { payload[r.key] = Number(payload[r.key] || 0); });
    try {
      if (showcase?.id) {
        await base44.entities.ShowcaseRig.update(showcase.id, payload);
      } else {
        const created = await base44.entities.ShowcaseRig.create(payload);
        setShowcase(created);
      }
      if (record?.id) {
        try {
          const coverUrl = galleryImages.find((p) => p.show_booking)?.url || galleryImages[0]?.url || '';
          await base44.entities.BookingRig.update(record.id, { gallery_images: galleryImages, image_url: coverUrl });
        } catch {}
      }
      setScSaved(true);
      setTimeout(() => setScSaved(false), 2000);
    } finally {
      setSavingSc(false);
    }
  };

  const handleRemoveShowcase = async () => {
    if (!showcase?.id) return;
    if (!window.confirm('Remove the showcase page for this rig? The booking rig stays intact.')) return;
    await base44.entities.ShowcaseRig.delete(showcase.id);
    setShowcase(null);
    setSc(EMPTY_SHOWCASE);
  };

  const handleDelete = async () => {
    if (!record?.id) return;
    if (!window.confirm(`Delete "${name}"? This permanently removes the booking rig and its showcase page. This cannot be undone.`)) return;
    setDeleting(true);
    try {
      if (showcase?.id) { try { await base44.entities.ShowcaseRig.delete(showcase.id); } catch {} }
      await base44.entities.BookingRig.delete(record.id);
      onDelete?.(record);
    } finally {
      setDeleting(false);
    }
  };

  const canDelete = !!record?.id && !defaultData;

  const addGame = () => {
    const g = gameInput.trim(); if (!g) return;
    setScField('supported_games', [...(sc.supported_games || []), g]); setGameInput('');
  };

  return (
    <div className="bg-[#111115] border border-white/5">
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-white/5 flex-wrap gap-3">
        <div className="min-w-0">
          <span className="text-primary text-[10px] font-bold tracking-widest block mb-1">{isNew ? 'NEW RIG' : (resolvedKey || 'RIG').toUpperCase()}</span>
          <h3 className="text-white font-bold text-base uppercase truncate">{name || 'Untitled Rig'}</h3>
          <div className="flex items-center gap-2 mt-2">
            {record?.id && (
              <a href={`/bookings?rig=${encodeURIComponent(resolvedKey)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-primary/30 text-primary hover:bg-primary/10 text-[10px] font-bold tracking-widest px-2.5 py-1 transition-colors">
                <ExternalLink className="w-3 h-3" /> PREVIEW BOOKING
              </a>
            )}
            {showcase ? (
              <a href={`/simulators/${showcase.slug || resolvedKey}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-primary/30 text-primary hover:bg-primary/10 text-[10px] font-bold tracking-widest px-2.5 py-1 transition-colors">
                <ExternalLink className="w-3 h-3" /> PREVIEW SHOWCASE
              </a>
            ) : null}
          </div>
        </div>
        <span className={`text-[10px] font-bold tracking-widest px-2.5 py-1 shrink-0 ${active ? 'bg-primary/10 text-primary' : 'bg-white/5 text-white/30'}`}>
          {active ? 'ACTIVE' : 'HIDDEN'}
        </span>
      </div>

      {/* Sub-tab switcher */}
      <div className="flex items-center gap-1 px-5 pt-4 border-b border-white/5 overflow-x-auto">
        <button onClick={() => setSubTab('booking')}
          className={`px-4 py-2.5 text-[10px] font-bold tracking-widest border-b-2 whitespace-nowrap transition-colors ${subTab === 'booking' ? 'border-primary text-primary' : 'border-transparent text-white/40 hover:text-white'}`}>
          BOOKING DETAILS
        </button>
        <button onClick={() => setSubTab('showcase')} disabled={isNew}
          className={`px-4 py-2.5 text-[10px] font-bold tracking-widest border-b-2 whitespace-nowrap transition-colors disabled:opacity-30 ${subTab === 'showcase' ? 'border-primary text-primary' : 'border-transparent text-white/40 hover:text-white'}`}>
          SHOWCASE PAGE {showcase ? '' : '(empty)'}
        </button>
      </div>

      <div className="p-5">
        {subTab === 'booking' ? (
          <RigBookingTab
            isNew={isNew}
            keyVal={key} onKey={setKey}
            name={name} onName={setName}
            tag={tag} onTag={setTag}
            description={description} onDescription={setDescription}
            specs={specs} onSpecs={setSpecs}
            galleryImages={galleryImages} onGalleryImages={setGalleryImages}
            sortOrder={sortOrder} onSortOrder={setSortOrder}
            active={active} onToggleActive={() => setActive((a) => !a)}
            canDelete={canDelete} deleting={deleting} onDelete={handleDelete}
            saving={savingBooking} saved={bookingSaved} onSave={handleSaveBooking}
          />
        ) : (
          <RigShowcaseTab
            scLoading={scLoading}
            showcase={showcase}
            resolvedKey={resolvedKey}
            name={name}
            onAiFill={handleAiFill} aiLoading={aiLoading}
            onRemoveShowcase={handleRemoveShowcase}
            sc={sc} onScField={setScField}
            galleryImages={galleryImages} onGalleryImages={setGalleryImages}
            gameInput={gameInput} onGameInput={setGameInput} onAddGame={addGame}
            saving={savingSc} saved={scSaved} onSave={handleSaveShowcase}
          />
        )}
      </div>
    </div>
  );
}